import { EventTypes } from 'redux/reducers/subscriptions';

import { EntityTypes } from 'context/entityContext';
import formatNumber from 'helpers/formatNumber';
import { apiTypeToEntityType } from 'helpers/mapEntityTypes';

export default getEventMessage;

function isInProgress(eventType: EventTypes): boolean {
  return eventType.includes('in_progress');
}

function getEventMessage(
  eventType: EventTypes
): (
  entityType: EntityTypes,
  entity: string,
  detailData: any
) => string | undefined {
  return function (
    entityType: EntityTypes,
    entity: string,
    detailData: {
      displayName: string;
      relatedEntity: {
        id: string;
        type: string;
        displayName: string;
      };
      values: {
        name: string;
        position: string;
        followers: string;
        customerName: string;
        threshold: string;
      };
    }
  ): string | undefined {
    const type = apiTypeToEntityType(entityType);
    const { displayName, relatedEntity, values } = detailData;
    const change = Number(values.threshold) > 0 ? 'increased' : 'decreased';

    switch (eventType) {
      case 'streaming_performance_threshold':
        {
          const formattedValue = formatNumber(
            Number(values.threshold),
            'number',
            true
          );

          if (entityType === 'track' || entityType === 'album') {
            return `Total ${type} streams for “${entity}” crossed ${formattedValue} overall.`;
          }
          if (entityType === 'artist') {
            return `Total streams for ${entity} crossed ${formattedValue} overall.`;
          }
        }
        break;
      case 'streaming_wow_in_progress_growth_threshold':
      case 'streaming_wow_completed_growth_threshold':
        {
          const formattedValue = formatNumber(
            Number(values.threshold),
            'percentage'
          );
          const period = isInProgress(eventType)
            ? 'the same period last week'
            : 'the week before';

          if (entityType === 'track' || entityType === 'album') {
            return `Weekly ${type} streams of “${entity}” have ${change} by ${formattedValue} compared to ${period}.`;
          }
          if (entityType === 'artist') {
            return `Weekly streams for ${entity} have ${change} by ${formattedValue} compared to ${period}.`;
          }
        }
        break;
      case 'streaming_daily_growth_threshold':
        {
          const formattedValue = formatNumber(
            Number(values.threshold),
            'percentage'
          );

          if (entityType === 'track' || entityType === 'album') {
            return `Daily ${type} streams of “${entity}” have ${change} by ${formattedValue} compared to yesterday.`;
          }
          if (entityType === 'artist') {
            return `Daily streams for ${entity} have ${change} by ${formattedValue} compared to yesterday.`;
          }
        }
        break;
      case 'downloads_album_wow_in_progress_growth_threshold':
      case 'downloads_album_wow_completed_growth_threshold': {
        const formattedValue = formatNumber(
          Number(values.threshold),
          'percentage'
        );
        const period = isInProgress(eventType)
          ? 'the same period last week'
          : 'the week before';

        return `Weekly album downloads for ${entity} have ${change} by ${formattedValue} compared to ${period}.`;
      }
      case 'downloads_track_wow_completed_growth_threshold':
      case 'downloads_track_wow_in_progress_growth_threshold': {
        const formattedValue = formatNumber(
          Number(values.threshold),
          'percentage'
        );
        const period = isInProgress(eventType)
          ? 'the same period last week'
          : 'the week before';

        return `Weekly track downloads for ${entity} have ${change} by ${formattedValue} compared to ${period}.`;
      }
      case 'downloads_album_daily_growth_threshold':
        {
          const formattedValue = formatNumber(
            Number(values.threshold),
            'percentage'
          );

          if (entityType === 'track' || entityType === 'album') {
            return `Daily ${type} downloads of “${entity}” have ${change} by ${formattedValue} compared to yesterday.`;
          }
          if (entityType === 'artist') {
            return `Daily album downloads for ${entity} have ${change} by ${formattedValue} compared to yesterday.`;
          }
        }
        break;
      case 'downloads_track_daily_growth_threshold':
        {
          const formattedValue = formatNumber(
            Number(values.threshold),
            'percentage'
          );

          if (entityType === 'track' || entityType === 'album') {
            return `Daily ${type} downloads of “${entity}” have ${change} by ${formattedValue} compared to yesterday.`;
          }
          if (entityType === 'artist') {
            return `Daily track downloads for ${entity} have ${change} by ${formattedValue} compared to yesterday.`;
          }
        }
        break;
      case 'downloads_album_performance_threshold':
        {
          const formattedValue = formatNumber(
            Number(values.threshold),
            'number',
            true
          );

          if (entityType === 'track' || entityType === 'album') {
            return `Total ${type} downloads for “${entity}” crossed ${formattedValue} overall.`;
          }
          if (entityType === 'artist') {
            return `Total album downloads for ${entity} crossed ${formattedValue} overall.`;
          }
        }
        break;
      case 'downloads_track_performance_threshold':
        {
          const formattedValue = formatNumber(
            Number(values.threshold),
            'number',
            true
          );

          if (entityType === 'track' || entityType === 'album') {
            return `Total ${type} downloads for “${entity}” crossed ${formattedValue} overall.`;
          }
          if (entityType === 'artist') {
            return `Total track downloads for ${entity} crossed ${formattedValue} overall.`;
          }
        }
        break;
      case 'physical_wow_completed_growth_threshold':
      case 'physical_wow_in_progress_growth_threshold':
        {
          const formattedValue = formatNumber(
            Number(values.threshold),
            'percentage'
          );
          const period = isInProgress(eventType)
            ? 'the same period last week'
            : 'the week before';

          if (entityType === 'album') {
            return `Weekly shipped units of “${entity}” have ${change} by ${formattedValue} compared to ${period}.`;
          }
          if (entityType === 'artist') {
            return `Weekly shipped units of ${entity} have ${change} by ${formattedValue} compared to ${period}.`;
          }
        }
        break;
      case 'physical_daily_growth_threshold':
        {
          const formattedValue = formatNumber(
            Number(values.threshold),
            'percentage'
          );

          if (entityType === 'track' || entityType === 'album') {
            return `Daily shipped units of “${entity}” have ${change} by ${formattedValue} compared to yesterday.`;
          }
          if (entityType === 'artist') {
            return `Daily shipped units of ${entity} have ${change} by ${formattedValue} compared to yesterday.`;
          }
        }
        break;
      case 'physical_performance_threshold':
        {
          const formattedValue = formatNumber(
            Number(values.threshold),
            'number',
            true
          );

          if (entityType === 'track' || entityType === 'album') {
            return `Total shipped units for ${type} “${entity}” crossed ${formattedValue}.`;
          }
          if (entityType === 'artist') {
            return `Total shipped units for ${entity} crossed ${formattedValue} overall.`;
          }
        }
        break;
      case 'playlist_add':
      case 'priority_playlist_add': {
        const formattedValue = formatNumber(
          Number(values?.followers),
          'number',
          true
        );
        return `${displayName} by ${relatedEntity.displayName} was added to ${values?.customerName}'s ${values?.name} (${formattedValue} followers) at position ${values?.position}.`;
      }
      case 'playlist_removal':
      case 'priority_playlist_removal': {
        const formattedValue = formatNumber(
          Number(values?.followers),
          'number',
          true
        );
        return `${displayName} by ${relatedEntity.displayName} is no longer in the top 100 on ${values?.customerName}'s ${values?.name} (${formattedValue} followers).`;
      }
      case 'playlist_new_peak': {
        const formattedValue = formatNumber(
          Number(values?.followers),
          'number',
          true
        );
        return `${displayName} by ${relatedEntity.displayName} reached peak position on ${values?.customerName}'s ${values?.name} (${formattedValue} followers) at position ${values?.position}.`;
      }
      case 'chart_new_peak': {
        return `${displayName} by ${relatedEntity.displayName} reached a peak position on ${values?.name} at position #${values?.position}.`;
      }
      case 'chart_removal': {
        return `${displayName} by ${relatedEntity.displayName} was removed from ${values?.name} at position #${values?.position}.`;
      }
      case 'chart_add': {
        return `${displayName} by ${relatedEntity.displayName} was added to ${values?.name} at position #${values?.position}.`;
      }
      default:
        return undefined;
    }
  };
}
